import type { ISerialize } from '@/objects/ISerialize';
import { formatDateIso } from '@/utilities/dateutils';

export class DynamicReportParameter implements ISerialize {
    public ID: number | null;

    public BoolValue: boolean | null;

    public DateValue: Date | null;

    public IntList: number[];

    public IntValue: number | null;

    public Name: string;

    public StringValue: string | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number | null;
        BoolValue?: boolean | null;
        DateValue?: Date | null;
        IntList?: number[];
        IntValue?: number | null;
        Name?: string | null;
        StringValue?: string | null;
    }) {
        this.ID = values.ID ?? null;

        this.BoolValue = values.BoolValue ?? null;

        this.DateValue = values.DateValue ?? null;

        this.IntList = values.IntList ?? [];

        this.IntValue = values.IntValue ?? null;

        this.Name = values.Name ?? '';

        this.StringValue = values.StringValue ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"BoolValue": ${JSON.stringify(this.BoolValue)}` +
            ',' +
            `"DateValue": ${this.DateValue ? '"' + formatDateIso(this.DateValue) + '"' : null}` +
            ',' +
            `"IntList": [ ${this.IntList.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"IntValue": ${JSON.stringify(this.IntValue)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"StringValue": ${JSON.stringify(this.StringValue)}` +
            '}'
        );
    }

    /**
     * Creates an instance of DynamicReportParameter from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number | null;
        BoolValue?: boolean | null;
        DateValue?: Date | null;
        IntList?: number[];
        IntValue?: number | null;
        Name?: string | null;
        StringValue?: string | null;
    }): DynamicReportParameter {
        return new DynamicReportParameter(values);
    }

    public static _FromServerJson(json: any): DynamicReportParameter {
        return new DynamicReportParameter({
            ID: json.ID ?? null,
            BoolValue: json.BoolValue ?? null,
            DateValue: json.DateValue ? (json.DateValue instanceof Date ? json.DateValue : new Date(json.DateValue)) : null,
            IntList: json.IntList,
            IntValue: json.IntValue ?? null,
            Name: json.Name ?? '',
            StringValue: json.StringValue ?? '',
        });
    }

    /**
     * Creates an instance of DynamicReportParameter from another instance of DynamicReportParameter.
     * @param other The DynamicReportParameter you want to clone
     * @returns A cloned DynamicReportParameter
     */
    public static FromOther(other: DynamicReportParameter): DynamicReportParameter {
        return DynamicReportParameter._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return DynamicReportParameter.FromOther(this);
    }

    static NAME_OF_CLASS = 'DynamicReportParameter';
}
